import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight,
  Sparkles, 
  Check,
  Scale,
  Loader2,
  ExternalLink,
  Info,
  Zap,
  Target,
  HelpCircle,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';
import { useCaseSuggestionsApi } from '@/services/api';
import { cn } from '@/lib/utils';
import { useSearchHistory } from '@/hooks/useSearchHistory';
import { SearchHistory } from '@/components/SearchHistory';

// Check if platform was recently updated (within 30 days)
const isRecentlyUpdated = (updatedAt) => {
  if (!updatedAt) return false;
  const updateDate = new Date(updatedAt);
  const now = new Date();
  const daysDiff = Math.floor((now - updateDate) / (1000 * 60 * 60 * 24));
  return daysDiff <= 30;
};

// Confidence indicator badge component
const ConfidenceBadge = ({ confidence }) => {
  const config = {
    high: {
      label: 'High confidence',
      description: 'Strong match to your needs',
      icon: Target,
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      iconColor: 'text-emerald-600'
    },
    medium: {
      label: 'Medium confidence',
      description: 'Good potential matches',
      icon: Zap,
      className: 'bg-amber-100 text-amber-700 border-amber-200',
      iconColor: 'text-amber-600'
    },
    low: {
      label: 'Exploratory',
      description: 'Based on keyword matching',
      icon: HelpCircle,
      className: 'bg-slate-100 text-slate-600 border-slate-200',
      iconColor: 'text-slate-500'
    }
  };

  const { label, description, icon: Icon, className, iconColor } = config[confidence] || config.medium;

  return (
    <div className="group relative">
      <Badge 
        variant="outline" 
        className={cn("gap-1.5 px-2.5 py-1 text-xs font-medium cursor-help", className)}
      >
        <Icon className={cn("w-3.5 h-3.5", iconColor)} />
        {label}
      </Badge>
      {/* Tooltip */}
      <div className="absolute right-0 top-full mt-2 w-48 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
        <p className="font-medium mb-0.5">{label}</p>
        <p className="text-muted-foreground">{description}</p>
      </div>
    </div>
  );
};

export default function AISearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const { history, addSearch, removeSearch, clearHistory } = useSearchHistory();

  // Save search to history when query changes
  useEffect(() => {
    if (query && query.length >= 5) {
      addSearch(query);
    }
  }, [query, addSearch]);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query || query.length < 5) {
        setError('Please provide a longer description of what you need.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await useCaseSuggestionsApi.aiRecommend(query);
        setResults(response);
      } catch (err) {
        setError('Failed to get recommendations. Please try again.');
        console.error('AI search error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [query]);

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleCompare = () => {
    if (selectedPlatforms.length >= 2) {
      navigate(`/compare?platforms=${selectedPlatforms.join(',')}`);
    }
  };

  const handleSelectAll = () => {
    if (results?.platforms) {
      setSelectedPlatforms(results.platforms.map(p => p.id));
    }
  };

  const handleClearAll = () => {
    setSelectedPlatforms([]);
  };

  // Handle search from history
  const handleHistorySelect = (selectedQuery) => {
    navigate(`/ai-search?q=${encodeURIComponent(selectedQuery)}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        pageType="custom"
        title={`AI Search Results | GoCompareAI`}
        description="AI-powered platform recommendations based on your needs."
      />
      
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Button>

        {/* Query Display */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-lg shadow-sky-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">AI Recommendations</h1>
              <p className="text-sm text-muted-foreground">Based on your search</p>
            </div>
          </div>
          
          <Card className="bg-sky-50 border-sky-200">
            <CardContent className="p-4">
              <p className="text-sky-900 font-medium" data-testid="current-query">"{query}"</p>
            </CardContent>
          </Card>
        </div>

        {/* Search History - Show when not loading and have history */}
        {!isLoading && history.length > 0 && (
          <SearchHistory
            history={history.filter(h => h.query.toLowerCase() !== query.toLowerCase())}
            onSelect={handleHistorySelect}
            onRemove={removeSearch}
            onClear={clearHistory}
            variant="full"
            className="mb-6"
          />
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-4 animate-pulse">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <p className="text-lg font-medium text-foreground mb-2">AI is analyzing your request...</p>
            <p className="text-sm text-muted-foreground">Searching through 400+ platforms to find the best matches</p>
            <Loader2 className="w-6 h-6 text-sky-500 animate-spin mt-4" />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="p-6 text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => navigate('/')}>Try Again</Button>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {results && !isLoading && (
          <>
            {/* AI Explanation with Confidence Badge */}
            {results.explanation && (
              <Card className="mb-6 border-sky-200 bg-gradient-to-r from-sky-50 to-blue-50">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <p className="font-semibold text-sky-900">Why these platforms?</p>
                        {results.confidence && (
                          <ConfidenceBadge confidence={results.confidence} />
                        )}
                      </div>
                      <p className="text-sky-800 leading-relaxed">{results.explanation}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Selection Controls */}
            {results.platforms && results.platforms.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    {selectedPlatforms.length} of {results.platforms.length} selected
                  </p>
                  <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                    Select all
                  </Button>
                  {selectedPlatforms.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleClearAll}>
                      Clear
                    </Button>
                  )}
                </div>
                
                <Button 
                  onClick={handleCompare}
                  disabled={selectedPlatforms.length < 2}
                  className="gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
                >
                  <Scale className="w-4 h-4" />
                  Compare Selected ({selectedPlatforms.length})
                </Button>
              </div>
            )}

            {/* Platform Cards */}
            {results.platforms && results.platforms.length > 0 ? (
              <div className="space-y-4">
                {results.platforms.map((platform, index) => (
                  <Card 
                    key={platform.id}
                    className={cn(
                      "transition-all cursor-pointer hover:shadow-lg",
                      selectedPlatforms.includes(platform.id) 
                        ? "ring-2 ring-sky-500 border-sky-300 bg-sky-50/50" 
                        : "hover:border-sky-200"
                    )}
                    onClick={() => togglePlatform(platform.id)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <div className="pt-1">
                          <Checkbox 
                            checked={selectedPlatforms.includes(platform.id)}
                            onCheckedChange={() => togglePlatform(platform.id)}
                            className="data-[state=checked]:bg-sky-500 data-[state=checked]:border-sky-500"
                          />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs bg-sky-100 text-sky-700 border-sky-200">
                                  #{index + 1} Match
                                </Badge>
                                {isRecentlyUpdated(platform.updatedAt) && (
                                  <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Updated
                                  </Badge>
                                )}
                              </div>
                              <h3 className="text-lg font-semibold text-foreground">{platform.name}</h3>
                              {platform.provider && (
                                <p className="text-sm text-muted-foreground">by {platform.provider}</p>
                              )}
                            </div>
                            
                            <Link 
                              to={`/platform/${platform.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 hover:underline"
                            >
                              View details
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </div>
                          
                          <p className="text-muted-foreground mb-3 leading-relaxed">
                            {platform.summary}
                          </p>
                          
                          {platform.tags && platform.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {platform.tags.map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Selection Indicator */}
                        {selectedPlatforms.includes(platform.id) && (
                          <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-muted-foreground mb-4">No platforms found matching your description.</p>
                  <Button onClick={() => navigate('/')}>Try a different search</Button>
                </CardContent>
              </Card>
            )}

            {/* Bottom Compare Button */}
            {results.platforms && results.platforms.length > 0 && selectedPlatforms.length >= 2 && (
              <div className="sticky bottom-4 mt-6">
                <Card className="bg-gradient-to-r from-sky-500 to-blue-600 border-0 shadow-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="text-white">
                        <p className="font-semibold">{selectedPlatforms.length} platforms selected</p>
                        <p className="text-sm text-sky-100">Ready to compare side by side</p>
                      </div>
                      <Button 
                        onClick={handleCompare}
                        size="lg"
                        className="bg-white text-sky-600 hover:bg-sky-50 gap-2"
                      >
                        <Scale className="w-5 h-5" />
                        Compare Now
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Hint */}
            {results.platforms && results.platforms.length > 0 && selectedPlatforms.length < 2 && (
              <p className="text-center text-sm text-muted-foreground mt-6">
                Select at least 2 platforms to compare them side by side
              </p>
            )}
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
